import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { User, Edit, Save, X, MapPin, Mail, Star, Award, Calendar } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { userApi } from '../services/api'
import type { UserUpdateRequest } from '../types'
import toast from 'react-hot-toast'

const ProfilePage = () => {
  const { t } = useTranslation()
  const { user, updateUser } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<UserUpdateRequest>({
    username: user?.username || '',
    city: user?.city || '',
    address: ''
  })

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        city: user.city || '',
        address: ''
      })
    }
  }, [user])

  const handleInputChange = (field: keyof UserUpdateRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value.trim() || undefined
    }))
  }

  const handleSave = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      // Only send fields that have actually changed
      const updates: UserUpdateRequest = {}
      
      if (formData.username !== user.username) {
        updates.username = formData.username
      }
      if (formData.city !== user.city) {
        updates.city = formData.city
      }
      if (formData.address) {
        updates.address = formData.address
      }

      if (Object.keys(updates).length === 0) {
        setIsEditing(false)
        return
      }

      const updatedUser = await userApi.updateProfile(updates)
      updateUser(updatedUser)
      setIsEditing(false)
      toast.success(t('profile.updateSuccess'))
    } catch (error) {
      toast.error(t('profile.updateError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    if (user) {
      setFormData({
        username: user.username || '',
        city: user.city || '',
        address: ''
      })
    }
    setIsEditing(false)
  }

  const getRatingDisplay = () => {
    if (!user?.rating_average || user.rating_count === 0) {
      return (
        <div className="flex items-center text-gray-500">
          <Star className="h-5 w-5 mr-1" />
          <span>{t('profile.noRatings')}</span>
        </div>
      )
    }

    return (
      <div className="flex items-center">
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-5 w-5 ${
                star <= user.rating_average! 
                  ? 'text-yellow-400 fill-current' 
                  : 'text-gray-300'
              }`}
            />
          ))}
        </div>
        <span className="ml-2 text-gray-700">
          {user.rating_average.toFixed(1)} ({user.rating_count} {t('profile.reviews')})
        </span>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-orange-600"></div>
          <p className="mt-4 text-lg text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">{t('profile.title')}</h1>
          <p className="text-xl text-gray-700 leading-relaxed">{t('profile.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-2xl border border-orange-100 p-8">
              <div className="text-center">
                <div className="mx-auto w-32 h-32 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full flex items-center justify-center mb-6 shadow-xl">
                  <User className="h-16 w-16 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {user.username || t('user.anonymous')}
                </h2>
                
                {user.city && (
                  <div className="flex items-center justify-center mt-2 text-gray-600">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{user.city}</span>
                  </div>
                )}

                <div className="mt-4">
                  {getRatingDisplay()}
                </div>

                <div className="mt-8 pt-6 border-t border-orange-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">{t('profile.credits')}</span>
                    <span className="font-bold text-orange-600 text-xl">
                      {user.credits} {t('common.credits')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-gray-600">{t('profile.memberSince')}</span>
                    <span className="text-gray-900">
                      {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-3xl shadow-2xl border border-orange-100 p-8 mt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {t('profile.quickActions')}
              </h3>
              <div className="space-y-4">
                <button className="w-full text-left p-4 rounded-2xl border-2 border-orange-200 hover:bg-orange-50 hover:border-orange-300 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mr-4">
                      <Calendar className="h-5 w-5 text-orange-600" />
                    </div>
                    <span className="text-gray-900 font-medium">{t('profile.viewBookings')}</span>
                  </div>
                </button>
                <button className="w-full text-left p-4 rounded-2xl border-2 border-orange-200 hover:bg-orange-50 hover:border-orange-300 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mr-4">
                      <Award className="h-5 w-5 text-orange-600" />
                    </div>
                    <span className="text-gray-900 font-medium">{t('profile.viewReviews')}</span>
                  </div>
                </button>
                <button className="w-full text-left p-4 rounded-2xl border-2 border-orange-200 hover:bg-orange-50 hover:border-orange-300 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mr-4">
                      <Mail className="h-5 w-5 text-orange-600" />
                    </div>
                    <span className="text-gray-900 font-medium">{t('profile.exportData')}</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-2xl border border-orange-100">
              <div className="p-8 border-b border-orange-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {t('profile.personalInfo')}
                  </h3>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center space-x-2 px-6 py-3 text-lg font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 border-0 rounded-2xl hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <Edit className="h-4 w-4" />
                      <span>{t('profile.edit')}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="p-8 space-y-8">
                {/* Username */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">
                    {t('profile.username')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.username || ''}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      className="w-full px-6 py-4 border-2 border-orange-200 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg shadow-sm"
                      placeholder={t('profile.usernamePlaceholder')}
                    />
                  ) : (
                    <p className="text-gray-900">
                      {user.username || (
                        <span className="text-gray-500 italic">{t('profile.notSet')}</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Email (read-only) */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">
                    {t('profile.email')}
                  </label>
                  <p className="text-gray-900 bg-orange-50 px-6 py-4 rounded-2xl border border-orange-200">
                    ***@***.*** <span className="text-sm text-gray-500">({t('profile.emailHidden')})</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('profile.emailNote')}
                  </p>
                </div>

                {/* City */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">
                    {t('profile.city')}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-6 py-4 border-2 border-orange-200 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg shadow-sm"
                      placeholder={t('profile.cityPlaceholder')}
                    />
                  ) : (
                    <p className="text-gray-900">
                      {user.city || (
                        <span className="text-gray-500 italic">{t('profile.notSet')}</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Address (only shown when editing) */}
                {isEditing && (
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-3">
                      {t('profile.address')}
                    </label>
                    <input
                      type="text"
                      value={formData.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full px-6 py-4 border-2 border-orange-200 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg shadow-sm"
                      placeholder={t('profile.addressPlaceholder')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('profile.addressPrivate')}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                {isEditing && (
                  <div className="flex space-x-4 pt-6">
                    <button
                      onClick={handleSave}
                      disabled={isLoading}
                      className="flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-3xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <Save className="h-5 w-5" />
                      <span>{isLoading ? t('common.saving') : t('profile.save')}</span>
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="flex items-center space-x-2 px-8 py-4 bg-white border-2 border-orange-200 text-gray-700 rounded-3xl hover:bg-orange-50 hover:border-orange-300 font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <X className="h-5 w-5" />
                      <span>{t('profile.cancel')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-3xl p-8 mt-8 shadow-lg">
              <h4 className="text-lg font-bold text-orange-900 mb-4">
                {t('profile.privacyTitle')}
              </h4>
              <div className="text-lg text-orange-800 space-y-3">
                <p>• {t('profile.privacy1')}</p>
                <p>• {t('profile.privacy2')}</p>
                <p>• {t('profile.privacy3')}</p>
              </div>
            </div>

            {/* Account Management */}
            <div className="bg-white rounded-3xl shadow-2xl border border-orange-100 mt-8">
              <div className="p-8 border-b border-orange-200">
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('profile.accountManagement')}
                </h3>
              </div>
              <div className="p-8 space-y-6">
                <button className="w-full text-left p-6 border-2 border-orange-200 rounded-2xl hover:bg-orange-50 hover:border-orange-300 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-lg">
                  <h4 className="font-semibold text-gray-900 text-lg">{t('profile.changePreferences')}</h4>
                  <p className="text-gray-600 mt-2">{t('profile.changePreferencesDesc')}</p>
                </button>
                
                <button className="w-full text-left p-6 border-2 border-orange-200 rounded-2xl hover:bg-orange-50 hover:border-orange-300 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-lg">
                  <h4 className="font-semibold text-gray-900 text-lg">{t('profile.downloadData')}</h4>
                  <p className="text-gray-600 mt-2">{t('profile.downloadDataDesc')}</p>
                </button>

                <button className="w-full text-left p-6 border-2 border-red-300 rounded-2xl hover:bg-red-50 hover:border-red-400 transition-all duration-300 transform hover:-translate-y-1 text-red-600 shadow-sm hover:shadow-lg">
                  <h4 className="font-semibold text-lg">{t('profile.deleteAccount')}</h4>
                  <p className="mt-2">{t('profile.deleteAccountDesc')}</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage